/* eslint-disable i18next/no-literal-string */
import React from 'react';
import { Box, ListItem, Text, UnorderedList } from '@chakra-ui/react';

import CommonModal from '../components/modal/CommonModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}
const TermsOfUse = (props: Props) => {
  const { isOpen, onClose } = props;

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Terms of Use"
      showCloseButton
      showSubmitButton={false}
      closeButtonVariant="brand"
      size="2xl"
      modalDescription="Last Updated: March 18, 2026 "
    >
      <Box maxH="60vh" overflowY="auto">
        <Text fontSize="sm">
          These terms and conditions of use are a binding contract between Entrata, Inc.
          (&quot;Entrata&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) and you
          (&quot;you&quot; or &quot;your&quot;). These terms and conditions of use, the Property
          Client&apos;s privacy notice, and any other agreement or document incorporated herein by
          reference (collectively, the &quot;Terms&quot;), govern your access to and use of
          ProspectPortal® websites and mobile websites (&quot;ProspectPortal Sites&quot;),
          ResidentPortal™ websites, mobile websites, and phone applications
          (&quot;ResidentPortal&quot;), any content, functionality, products, and services offered
          on or through the ProspectPortal Sites and ResidentPortal, and artificial intelligence
          leasing and resident assistant services, such as virtual messaging assistants
          (collectively, the &quot;Services&quot;).
        </Text>

        <Box bg="yellow.100" p={4} borderRadius="md" mt={4}>
          <Text fontSize="sm" fontWeight="bold" color="red.600">
            YOU SHOULD READ THESE TERMS CAREFULLY AS THEY CONTAIN AN ARBITRATION PROVISION GOVERNING
            HOW CLAIMS BETWEEN YOU AND US CAN BE BROUGHT. THESE PROVISIONS WILL, WITH LIMITED
            EXCEPTION, REQUIRE YOU TO: (1) WAIVE YOUR RIGHT TO A COURT HEARING AND A JURY TRIAL, (2)
            SUBMIT CLAIMS YOU HAVE AGAINST US TO BINDING AND FINAL ARBITRATION ON AN INDIVIDUAL
            BASIS, AND (3) WAIVE YOUR RIGHT TO PARTICIPATE IN ANY CLASS ACTION LAWSUIT TO ENFORCE
            THESE TERMS, MEANING THAT YOU AND WE ARE EACH GIVING UP THE RIGHT TO SUE EACH OTHER IN
            COURT OR IN CLASS ACTIONS OF ANY KIND. IN ARBITRATION, THERE IS NO JUDGE OR JURY. IF YOU
            DO NOT AGREE TO BE BOUND BY THESE TERMS, YOU MAY NOT USE OR ACCESS THE SERVICES.
          </Text>
        </Box>

        <Text fontSize="sm" mt={4}>
          BY ENTERING INTO THESE TERMS, YOU AND ENTRATA MUTUALLY AGREE TO WAIVE OUR RESPECTIVE
          RIGHTS TO RESOLUTION OF DISPUTES IN A COURT OF LAW BY A JUDGE OR JURY AND AGREE TO RESOLVE
          ANY DISPUTE BY ARBITRATION, as set forth below.
        </Text>

        <Text fontSize="sm" mt={4}>
          By using the Services, whether by creating an account, browsing ProspectPortal Sites,
          using our mobile applications, or otherwise, you agree that you have read and agree to be
          bound by these Terms. If you do not agree to these Terms in their entirety, your sole
          option is to discontinue your use of the Services.
        </Text>

        <Text fontSize="sm" mt={4}>
          We may update the Terms from time to time, which will be posted to the Services. By using
          the Services following the effective date of any modification to these Terms, you consent
          to the modified Terms.
        </Text>

        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            Quick Links
          </Text>
          <UnorderedList>
            <ListItem fontSize="sm">Payment Terms</ListItem>
            <ListItem fontSize="sm">Text Messaging (SMS) Terms</ListItem>
          </UnorderedList>
        </Box>

        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            DEFINITIONS
          </Text>
          <Text fontSize="sm" mt={2}>
            <Text as="span" fontWeight="bold">
              Entrata:
            </Text>{' '}
            The software company that hosts and maintains the Services on behalf of a Property
            Client.
          </Text>
          <Text fontSize="sm" mt={2}>
            <Text as="span" fontWeight="bold">
              Property Client:
            </Text>{' '}
            The legal entity that owns or manages the property displayed on the Services.
          </Text>
          <Text fontSize="sm" mt={2}>
            <Text as="span" fontWeight="bold">
              Payment Gateway:
            </Text>{' '}
            Refers to the electronic systems, through which you may pass payment information to us,
            including but not limited to name, mailing address, email address, and dollar amount of
            payments received.
          </Text>
          <Text fontSize="sm" mt={2}>
            <Text as="span" fontWeight="bold">
              Payment Processor:
            </Text>{' '}
            EntrataPay, LLC, a wholly-owned subsidiary of Entrata, and the payment processor
            appointed by Entrata to process your payments. Either Entrata or Payment Processor will
            process your payment and will be identified in the Payment Gateway as the processor. For
            purposes of Section 7 of these Terms, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;
            also includes Payment Processor.
          </Text>
        </Box>

        {/* Section 1 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            1. Agreement to Deal Electronically; Electronic Communications and Notices.
          </Text>
          <Text fontSize="sm" mt={3}>
            All of your transactions with or through the Services may, at our option, be conducted
            electronically from start to finish. If we decide to proceed non-electronically, those
            services will still be governed by the remainder of these Terms unless you enter into
            different terms on a form provided by us. If the law allows you to withdraw this consent
            or if we are ever required to deal with you non-electronically, we reserve the right to
            charge or increase fees and you agree to print or make an electronic copy of the Terms
            and any other contract or disclosure that we are required to provide to you.
          </Text>
          <Text fontSize="sm" mt={3}>
            You understand that by clicking the box titled &quot;I have read and accept the Terms
            &amp; Conditions&quot;, &quot;Create Account&quot;, &quot;Submit&quot;, or any similar
            derivation thereof, you are agreeing to conduct an electronic transaction and to use and
            receive notices, communications, disclosures, and records through electronic means. You
            agree to enter the requested information electronically via the Internet, and to be
            notified regarding any transaction electronically through the email address you have
            provided. You understand that electronically signed documents have the same legal effect
            as hard copies with ink signatures. You understand that you may refuse to conduct other
            electronic transactions in the future. Your transaction, if applicable, is subject to
            your acceptance of these Terms, which are agreed upon when you proceed with your
            transaction.
          </Text>
          <Text fontSize="sm" mt={3}>
            You further agree as follows:
          </Text>
          <UnorderedList mt={2} spacing={2}>
            <ListItem fontSize="sm">
              By making your payment and providing the information needed to process your checking,
              savings account, credit card account, or other payment account, you acknowledge that
              you have read and understand the Terms and conditions contained herein.
            </ListItem>
            <ListItem fontSize="sm">
              Except as otherwise provided in these Terms, we will give you any notices regarding
              the Services by posting them on the Services. You also authorize Entrata to send
              notices (including notice of subpoenas or other legal process, if any) via electronic
              mail. You must check the Services for notices, and you will be considered to have
              received a notice when it is posted on the Services, or when sent by us via electronic
              mail, whether or not received by you. You must keep your email address current and any
              notice sent by us to an email address that you have provided to us will be considered
              effective notice.
            </ListItem>
          </UnorderedList>
          <Text fontSize="sm" mt={3}>
            On occasion, Property Clients using our Services may call or text you to respond to your
            inquiry, provide you with application updates, provide reminders about your account,
            provide maintenance and other property updates, and other important information. For
            more information, see Section 16 below.
          </Text>
          <Text fontSize="sm" mt={3}>
            You agree that Property Clients may use our Services to communicate with you at any
            telephone numbers or email addresses you provide to us or through your account. Such
            communications may include reminders and notices about your rent payments, balances, and
            other obligations. While some communications are automatically generated by a virtual
            assistant, Property Clients may also communicate with you directly through channels that
            Entrata provides. You agree that Entrata is not responsible or liable for the content of
            these messages. You authorize Entrata to share with Property Clients any telephone
            numbers or email addresses you have provided to us for this purpose.
          </Text>
          <Text fontSize="sm" mt={3}>
            We use artificial intelligence and machine learning to deliver some of our Services.
            When you interact with the Services, you may be engaging with virtual assistants or
            other services powered by artificial intelligence or machine learning algorithms. When
            you engage with virtual assistants, you acknowledge that you are doing so at your own
            discretion and risk. Entrata does not warrant or guarantee that any information provided
            using such technology is accurate, complete, reliable, current, or error-free.
          </Text>
        </Box>

        {/* Section 2 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            2. Your Account
          </Text>
          <Text fontSize="sm" mt={3}>
            In order to use certain features of the Services, you may have to register and create a
            password-protected account.
          </Text>
          <Text fontSize="sm" mt={3}>
            In order to use the Services, you must be at least 18 years of age (or have parental
            consent). If you choose to create an account, you must provide information to us that is
            true, accurate, and current, and you must keep your registration and contact information
            up to date. If you make any Submissions, you must have all rights to own them, or,
            alternatively, you have sufficient rights in your Submissions to grant us the rights
            described in these Terms. You are responsible for and must protect the security of your
            account and password. You will be responsible for all use of your password or other
            credentials, even if such use was conducted without your authority or permission. When
            using the Services, you agree to abide by all laws and these Terms.
          </Text>
          <Text fontSize="sm" mt={3}>
            To enhance account security, we may require you to authenticate your account using
            multi-factor authentication (&quot;MFA&quot;). As a result, you may be required to use
            at least two forms of authentication when accessing your account. Forms of MFA in
            addition to your login credentials may include verification tokens delivered through
            short message service or a specified and supported two-factor authentication
            (&quot;2FA&quot;) application. If you choose to install and use a 2FA application on a
            device (e.g., phone or tablet), you do so at your own risk.
          </Text>
          <Text fontSize="sm" mt={3}>
            All instructions transmitted by or received from anyone presenting your password on the
            Services are binding to you. You agree that you are solely responsible for all
            transactions or acts that are validated through use of your password, whether or not
            made with your knowledge or authority. You agree not to let others use your account. You
            agree not to disclose your password to anyone and to guard your password carefully, with
            the full awareness that a failure to keep it secure will enable others to engage in
            transactions through the Services for which you will be legally responsible. If you ever
            suspect that someone may have obtained access to your password who is not intended to
            have authority to act on your behalf, please contact Entrata immediately. You can
            authorize Entrata to refuse to accept any further transactions initiated under that
            password on your behalf and Entrata will use commercially reasonable efforts to block
            such transactions.
          </Text>
          <Text fontSize="sm" mt={3}>
            Subject to applicable law, we may, at any time and without notice to you, terminate or
            block your access to the Services for any reason, in our sole discretion, including but
            not limited to the following reasons:
          </Text>
          <UnorderedList mt={2} spacing={2}>
            <ListItem fontSize="sm">
              We believe in our sole discretion that you have violated these Terms or have otherwise
              engaged in any activities that may harm or damage the reputation, rights, person, or
              property of the Services, Property Clients, our users, or any other person;
            </ListItem>
            <ListItem fontSize="sm">
              Requested by law enforcement or other government agencies; or
            </ListItem>
            <ListItem fontSize="sm">Your account has extended periods of inactivity.</ListItem>
          </UnorderedList>
          <Text fontSize="sm" mt={3}>
            You declare the following:
          </Text>
          <Text fontSize="sm" mt={2} ml={4}>
            a. That all representations and statements made by you or on your behalf in these Terms,
            or in any other document relating hereto, are true, accurate and complete in all
            material respects.
          </Text>
          <Text fontSize="sm" mt={2} ml={4}>
            b. Where applicable and appropriate, you agree to maintain and promptly update your
            registration or payment information to keep it true, accurate, current, and complete.
          </Text>
          <Text fontSize="sm" mt={2} ml={4}>
            c. Failure to provide or maintain accurate and current data that is being passed to and
            from you will result in breach even if we have been advised of the possibility thereof.
          </Text>
        </Box>

        {/* Section 3 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            3. Entrata&apos;s Role
          </Text>
          <Text fontSize="sm" mt={3}>
            Important: Entrata is a service provider. Entrata provides the Services to you on behalf
            of Property Clients. Entrata does not own or manage the properties listed on the
            Services and is not a party to rental or sale contracts for those properties, though the
            Services may lead you to use certain products or services provided by Entrata which will
            be clearly disclosed. As such, Entrata shall not be liable for the information, the
            content, the documents, or any other information or interaction by, between, or among
            you, other users of the Services, or Property Clients. You are responsible for how you
            use the Services, and we encourage you to exercise sound judgment when entering into
            rental property transactions.
          </Text>
          <Text fontSize="sm" mt={3}>
            In the event you have a dispute with a Property Client, you should resolve that dispute
            with the Property Client.
          </Text>
        </Box>

        {/* Section 4 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            4. License to Use the Services
          </Text>
          <Text fontSize="sm" mt={3}>
            We hereby grant you a limited, revocable, non-transferable, non-sublicensable license,
            under the rights Entrata has in the Service&apos;s content, to view and use the Services
            solely for the purpose of acquiring information in accordance with the Terms. The
            alteration, removal, or obliteration of any copyright and trademark notices is strictly
            prohibited. As between you and Entrata, we retain all right, title, and interest in and
            to the Services. Except as provided in these Terms, permission to reprint or
            electronically reproduce any content in whole or in part for any other purpose is
            expressly prohibited. The Services and all content contained therein is protected by
            copyright and intellectual property rights under both United States and foreign laws and
            all rights not expressly granted are reserved by Entrata, its affiliates, and its
            partners.
          </Text>
          <Text fontSize="sm" mt={3}>
            The license in this section does not include permission to copy the design elements,
            &quot;look and feel&quot; or layout of the Services. Those elements are protected by
            law, such as trade dress, trademark, unfair competition, and other laws, and may not be
            copied or imitated in any manner. Except as expressly provided in these Terms, neither
            Entrata nor any third party has conferred upon you any license or right under any
            patent, copyright, trademark, trade secret or any other proprietary right.
          </Text>
          <Text fontSize="sm" mt={3}>
            We do not guarantee uninterrupted or error-free operation of the Services or any portion
            thereof, but we will use reasonable efforts to maintain its operation and availability.
          </Text>
        </Box>

        {/* Section 5 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            5. Submissions and Content
          </Text>
          <Text fontSize="sm" mt={3}>
            Any comments or information that you provide to Entrata, for example, feedback or ideas
            in response to a customer survey regarding the Services, product or content reviews,
            suggestions, ideas, concepts, or other information are collectively deemed
            &quot;Submissions&quot;. None of the Submissions will be subject to any obligation of
            confidence on our part, and we will not be liable for any use or disclosure (including
            publication in any medium) of any Submissions. You hereby grant us a royalty-free,
            perpetual, irrevocable, world-wide license to use, copy, reproduce, create derivative
            works from, adapt, modify, publish, edit, translate, sell, distribute, transmit,
            transfer, publicly display, publicly perform, and display the Submissions without any
            limitation and in any media or any form now known or later developed. Without limiting
            the foregoing, we will be entitled to unrestricted use of the Submissions for any
            purpose, commercial or otherwise, without compensation to the provider of the
            Submissions. We may use the Submissions, in addition to any other data you provide or
            that is collected from you in accordance with our Property Clients&apos; instructions,
            including to train, develop, and improve our products and services powered by artificial
            intelligence. Further, we may sublicense these rights to third parties. We reserve the
            right to disclose any information concerning your use of the Services to the extent
            required by law or judicial or government order. From time-to-time, the Services may
            request information through the use of forms. Use of such forms is completely voluntary.
            Information requested may include, but is not limited to, contact information,
            demographic information and/or opinions.
          </Text>
          <Text fontSize="sm" mt={3}>
            You understand that all property listings, lease agreements, rental terms, financing
            terms, postings, messages, text, voice commands, images, photos, files, video, and other
            information, materials or documents posted on the Services or transmitted through or in
            connection with the Services by property managers, owners, users, advertisers, or others
            (the &quot;Third Party Content&quot;) are the sole responsibility of the Property
            Clients, users, advertisers, or others from whom such content originated. Entrata has no
            responsibility or duty to review, approve, or pre-screen any Third Party Content posted
            on the Services (including by Property Clients), and Entrata is not responsible for any
            such content. The views and opinions they express are solely those of the original
            contributor. Entrata does not guarantee the nature, safety, quality, condition, or
            management of any rental properties or units on the Services.
          </Text>
          <Text fontSize="sm" mt={3}>
            Under no circumstance are we responsible for any Submission or Third Party Content on
            the Services, nor shall we be held liable for any of the claims made therein. We are not
            responsible for the content or accuracy of any Submissions or Third Party Content, and
            the views and opinions they express are solely those of the original contributor. You
            are solely responsible for evaluating the truth or accuracy of any Submission or Third
            Party Content.
          </Text>
          <Text fontSize="sm" mt={3}>
            Entrata reserves the right in its sole discretion, but does not have any obligation, to
            delete or refuse to post any Submission or Third Party Content that violates the letter
            or spirit of any applicable agreements between Entrata and Property Clients, users,
            advertisers, or others posting or seeking to post any content, or for any other reason.
          </Text>
        </Box>

        {/* Section 6 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            6. Prohibited Content and Conduct
          </Text>
          <Text fontSize="sm" mt={3}>
            You must not, and may not allow any third party to, do or attempt to do any of the
            following:
          </Text>
          <UnorderedList mt={2} spacing={2}>
            <ListItem fontSize="sm">
              post to the Services or provide any Submission or Third Party Content that is or
              appears to be untrue, misleading, harmful, threatening, abusive, harassing,
              defamatory, vulgar, obscene, pornographic, hateful, discriminatory, invasive of
              another person&apos;s privacy or publicity rights, or otherwise objectionable, or that
              would constitute or encourage a criminal offense, give rise to liability, or violate
              any law;
            </ListItem>
            <ListItem fontSize="sm">
              use the Services in violation of any applicable local, state, national or
              international law (including export laws), contracts, intellectual property rights or
              that constitutes the commission of a tort, or for any purpose that is harmful or
              unintended (by us), or other than in full compliance with these Terms;
            </ListItem>
            <ListItem fontSize="sm">
              infringe upon a third party&apos;s intellectual property rights, including any patent,
              trademark, trade secret, copyright, right of publicity, including any content that is
              the subject of any claim of infringement;
            </ListItem>
            <ListItem fontSize="sm">
              provide, disclose, or transmit information of a type that you do not have a right to
              transmit under any law or under contractual or fiduciary relationships, such as
              proprietary and confidential information;
            </ListItem>
            <ListItem fontSize="sm">
              perform unsolicited, undisclosed or unauthorized advertising;
            </ListItem>
            <ListItem fontSize="sm">
              transmit any software viruses or any other computer code, files, or programs designed
              to interrupt, destroy, or limit the functionality of any computer software or hardware
              or telecommunications equipment; harmful, illegal, deceptive or disruptive code such
              as a virus, &quot;spyware,&quot; &quot;adware&quot; or other code that could adversely
              impact the Services or any recipient;
            </ListItem>
            <ListItem fontSize="sm">
              access, tamper with, or use services or areas of the Services that you are not
              authorized to access;
            </ListItem>
            <ListItem fontSize="sm">
              access or use any portion of the Services if you are a direct or indirect competitor
              of ours, including without limitation companies involved in data research, internet
              listing services, dissemination of information, or property management software;
            </ListItem>
            <ListItem fontSize="sm">alter information on or obtained from the Services;</ListItem>
            <ListItem fontSize="sm">
              reproduce, copy, display, store, perform, re-post, publish, transmit, distribute,
              sell, offer for sale, license, modify, create derivative works, tamper with, or
              otherwise use any portion of the postings, registration information, profiles,
              submissions or content belonging to the Services or other users of the Services, other
              than for your own personal, non-commercial use;
            </ListItem>
            <ListItem fontSize="sm">
              use any robot, spider, scraper or other automated means or interface not provided by
              us to access, retrieve, scrape, extract, gather, or index any portion of the Services;
            </ListItem>
            <ListItem fontSize="sm">
              transmit any unsolicited advertising, &quot;junk mail,&quot; &quot;spam,&quot; or
              &quot;chain letters&quot;;
            </ListItem>
            <ListItem fontSize="sm">
              frame any part of the Services, or link to the Services, or otherwise make it look
              like you have a relationship to us or that we have endorsed you or your content for
              any purpose except as expressly permitted in writing by us;
            </ListItem>
            <ListItem fontSize="sm">
              impersonate or misrepresent your affiliation with any person or entity;
            </ListItem>
            <ListItem fontSize="sm">
              reverse engineer any licensed software, application, or any other aspect of the
              Services or do anything that might discover source code, or bypass or circumvent
              measures employed to prevent or limit access to any area, content, or code of the
              Services; or
            </ListItem>
            <ListItem fontSize="sm">
              take any action which might impose a significant burden (as determined by us) on the
              Services&apos; infrastructure or computer systems, or otherwise interfere with the
              ordinary operation of the Services.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 7 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            7. Payments
          </Text>
          <Text fontSize="sm" mt={3}>
            7.1 If payment is made by ACH, including eCheck, you understand that if such payment is
            returned from the bank for any reason, you may be charged additional returned item fees
            (i.e., non-sufficient funds or &quot;NSF&quot;) by us or your Property Client, which
            will be added to the original amount and auto-debited from your bank account at least
            ten days after the initial payment fails. In the event that subsequent fee collection
            auto-debits also fail, you may continue to incur fees related to such attempts. You
            assume all responsibility for all such fees. Returned item fees vary; please contact
            your Property Client for the amount. Additional fees may also be assessed by your bank
            and/or Property Client. Your copy of the application will serve as a receipt for the
            application fee collected.
          </Text>
          <Text fontSize="sm" mt={2}>
            Oregon Residents Only: Entrata is acting as Payment Processor&apos;s authorized delegate
            with respect to your ACH payments.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.2 Convenience fees are charged by us or a third-party payment provider, and not by
            your Property Client. Convenience fees are not surcharges and are non-refundable. We are
            a third-party vendor, who is not the seller, lessor, or management company. Such
            convenience fees are not being charged based on any method of payment. Any payment
            methods initiated through the online platform may be charged a convenience fee including
            credit card, debit card, electronic checks, Cash App, and cash payments. These fees are
            being charged by us or a third-party payment provider for providing the convenience of
            an online payment channel. You agree that convenience fees may also apply to amounts
            refunded directly to your payment card, such as security deposit refunds.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.3 If you are applying for a lease, no guarantees are made to you that a property
            rental is or will be made available. You understand that you will acquire no rights in
            or to a property rental until you accept and sign a rental agreement and pay all
            applicable application fees and security deposits.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.4 You authorize the Property Client to obtain such credit reports, criminal histories,
            character reports, verification of rental and employment history as it deems necessary
            to verify all information in your application. You further understand that false,
            fraudulent, misleading or incomplete information may be grounds for denial of tenancy or
            subsequent eviction. There are no warranties offered by us or the Property Client,
            whether express or implied.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.5 You hereby authorize Entrata or Payment Processor (if applicable), on behalf of the
            Property Client, to initiate transaction entries, including any convenience fees noted
            herein, to your payment account number (including checking and savings accounts) and/or
            charges to your credit card. This billing will occur at the time of payment of each
            transaction.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.6 You hereby acknowledge that we, any third-party payment provider, and/or the
            Property Client may apply a convenience fee with each transaction initiated through the
            Payment Gateway. We or any third-party payment provider may amend this convenience fee
            at any time with or without notice. All convenience fees will be displayed on the
            payment screen prior to finalizing your transaction.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.7 We and your Property Client make every effort to comply with all payment processing
            rules and regulations. If you believe you have been charged a convenience fee in error,
            please contact the Property Client to whom your payment is submitted.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.8 We advise you that your credit card, bank account, or other payment account billing
            statement will show a charge item that displays the name of the merchant account of the
            payee. If you are unsure of that name, you agree to contact payee before processing this
            transaction so as to be sure to recognize the transaction when it appears on your
            monthly statement.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.9 We reserve the right to cancel your transaction processing account at any time for
            any reason. Your payee may also decline/refuse any and all payments at any time and for
            any reason at which time we will return the payment amount entered into the Payment
            Gateway, minus convenience fees paid, in an appropriate and reasonable time frame.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.10 We reserve the right to refuse processing service to you at any time for any
            reason. You also maintain the ability to discontinue use of the Payment Gateway at any
            time for any reason. Active recurring payments must be deleted before use of service is
            discontinued. It is your responsibility to terminate any and all recurring payments on
            your account before relocating or moving - otherwise charges will continue to be
            assessed to your billing account during the period designated for recurring payments to
            take place. You agree to defend, indemnify and hold harmless Entrata, Payment Processor,
            Property Client, their affiliates, licensors and service providers, and its and their
            respective officers, directors, employees, contractors, agents, licensors, suppliers,
            successors and assigns for any recurring payments that are charged because you failed to
            cancel your recurring payment. You can cancel your service by contacting your Property
            Client. We, in our sole discretion, may cancel this service without notice.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.11 If you believe a transaction was made improperly, we in our sole discretion may
            void, issue a credit, or rescind any transaction made through the Payment Gateway prior
            to remittance of such payment to your payee. Payment disputes arising after payment has
            been settled to your payee are between you and payee alone. We may act as an
            intermediary if such actions can reasonably and efficiently handle said problem.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.12 Both you and payee mutually reserve the right to cancel, reverse, or halt any and
            all checking or savings account, other payment account, or credit card transactions that
            have been cleared through the Payment Gateway.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.13 Entrata and Payment Processor will comply with all applicable Payment Card Industry
            Data Security Standards (PCI-DSS). For purposes of PCI-DSS compliance, we are
            responsible for all cardholder data it possesses, stores, processes, or transmits on
            your behalf. Such cardholder data includes account numbers, card expiration dates, and
            CVV2 data. Unless otherwise noted, all personally identifiable information is used
            solely for the normal business purpose of offering and rendering services.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.14 Entrata and Payment Processor act as the Property Client&apos;s authorized agent in
            collecting payment owed by you to the Property Client. Payment by you to Entrata or
            Payment Processor shall be considered payment to the Property Client, extinguishing your
            payment obligation to the Property Client (in the amount paid by you) as if you had paid
            the Property Client directly.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.15 By signing up for recurring payments, you authorize us to charge the designated
            payment method on the selected date, and re-attempt payment for five business days
            thereafter if prior attempts are unsuccessful. Additionally, you consent to our storage
            of your card or bank information to facilitate future recurring payments.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.16 You may not use the Payment Gateway for any illegal purpose or in any manner
            inconsistent with the terms and conditions set forth in these Terms. You agree that our
            Services shall only be used for lawful purposes. Any transaction or transmission which
            violates federal, state, or local laws is expressly prohibited.
          </Text>
          <Text fontSize="sm" mt={3}>
            7.17 When you provide a check as payment, you authorize us to use information from your
            check to make a one-time electronic fund transfer from your account. In certain
            circumstances, such as for technical or processing reasons, we may process your payment
            as a check transaction. When we use information from your check to make an electronic
            fund transfer, funds may be withdrawn from your account as soon as the same day we
            receive your payment, and you will not receive your check back from your financial
            institution.
          </Text>
        </Box>

        {/* Section 8 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            8. Third Party Products; Affiliate Products; Rental Agreements
          </Text>
          <Text fontSize="sm" mt={3}>
            <Text as="span" fontWeight="bold">
              Third Party Products.
            </Text>{' '}
            The Services may contain links, advertisements, or integrations to other websites,
            services, and products provided by third parties (&quot;Third Party Products&quot;).
            Such Third Party Products are provided for your convenience only. We have no control
            over Third Party Products and accept no responsibility for them or for any loss or
            damage that may arise from your use of them. Your use, access, or purchase of any Third
            Party Product is entirely at your own risk, and subject to separate terms of use and/or
            other agreements between you and the provider of such Third Party Product (a &quot;Third
            Party Service Provider&quot;). You are solely responsible for your decision to permit
            any Third Party Service Provider to use or access your data.
          </Text>
          <Text fontSize="sm" mt={3}>
            Entrata has no liability or responsibility whatsoever for any Third Party Products,
            including their accuracy, reliability, availability, security, data handling, data
            processing, completeness, usefulness, or quality, even if Entrata has reviewed,
            certified, or approved the Third Party Product for use in connection with the Services.
            The Third Party Service Provider is solely responsible for ensuring that any information
            submitted through their websites, products, services, or third party applications to the
            Services is accurate, complete, and correct. Entrata is not responsible for the
            standards or business practices of any Third Party Service Provider.
          </Text>
          <Text fontSize="sm" mt={3} fontWeight="bold">
            WE DISCLAIM ALL LIABILITY AND RESPONSIBILITY FOR ANY THIRD PARTY PRODUCTS (WHETHER
            SUPPORT, AVAILABILITY, SECURITY OR OTHERWISE) OR FOR THE ACTS OR OMISSIONS OF ANY THIRD
            PARTY SERVICE PROVIDER.
          </Text>
          <Text fontSize="sm" mt={3}>
            <Text as="span" fontWeight="bold">
              Affiliate Products.
            </Text>{' '}
            The Services may also contain links, advertisements, or integrations to other websites,
            services, and products that are provided by our affiliates or subsidiaries
            (&quot;Affiliate Products&quot;). Such Affiliate Products may be subject to separate
            terms of use and/or other agreements. For example, in connection with rental
            applications made on the ProspectPortal Sites, you may authorize an affiliate to conduct
            a background screening. You have certain rights in relation to this background screening
            which are provided to you separately.
          </Text>
          <Text fontSize="sm" mt={3}>
            <Text as="span" fontWeight="bold">
              Rental Agreements.
            </Text>{' '}
            Any rental agreements (including those involving financing, if any) that you enter into
            through the ProspectPortal Sites (including but not limited to those agreements for
            which you electronically sign a lease or rental agreement) are for services provided by
            the Property Client. Although Entrata provides the ProspectPortal Sites as a platform
            for the Services through which you may execute rental or lease agreements and enter into
            binding contracts with Property Clients, Entrata is not involved in, is not liable for,
            and is not a party to those transactions. The terms of those transactions are determined
            solely by you and the Property Client, and not by Entrata. You acknowledge that you will
            address any issues or concerns with such agreements with said Property Client, rather
            than Entrata. If you choose to use the electronic signature execution functionality of
            the ProspectPortal Sites, you understand, acknowledge and agree that you have carefully
            reviewed the disclosure relating to use of your electronic signature to execute
            documents and will not execute such documents with your electronic signature without
            providing your consent to use your electronic signature. You agree that you will rely on
            your own legal counsel to determine the sufficiency of the electronic signature
            execution of the documents and their enforceability and that Entrata makes no warranty
            or other representation with respect to the sufficiency of the electronic signature
            execution of the documents under applicable international, national, federal, state,
            provincial or local laws or regulations.
          </Text>
        </Box>

        {/* Section 9 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            9. Children
          </Text>
          <Text fontSize="sm" mt={3}>
            Nothing on the Services is intended to specifically solicit information from minors
            (people under 18 years old) or to seek to determine whether the visitor is a minor. If
            you are under 18, you are not permitted to submit to us any personally identifiable
            information, such as your name, address, email address, telephone number or any other
            information that would permit us to identify you. If you are under 14, it is against the
            law for us to ask you for that information, and we will not ask for it, nor do we want
            it. Anyone under 18 who wants to obtain any product from Entrata or otherwise submit any
            personally identifiable information to us should ask a parent or guardian to do it in
            his/her own name. Because such information will not be specifically identified as being
            from minors, users of the Services should be aware that personally identifiable
            information submitted to it by minors may be treated in the same manner as information
            given by an adult. If we learn we have collected or received personal information from a
            child under the age of 14 without verification of parental consent, we will delete that
            information. If you believe we might have any information from or about a child under
            the age of 14, please contact your Property Client.
          </Text>
        </Box>

        {/* Section 10 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            10. Privacy
          </Text>
          <Text fontSize="sm" mt={3}>
            By agreeing to these Terms, you hereby acknowledge you have received and reviewed a copy
            of the applicable Property Client&apos;s and Entrata&apos;s privacy policies.
          </Text>
          <Text fontSize="sm" mt={3}>
            Important: Entrata&apos;s privacy notice does not apply to end users of our Services
            (i.e., prospects and residents). Our privacy obligations with respect to such end users
            are governed solely by the agreements under which the Services are provided to our
            Property Clients and applicable law. Entrata generally has no direct relationship with
            end users, and we merely host or process such end user information on behalf of our
            Property Clients in our capacity as a service provider.
          </Text>
          <Text fontSize="sm" mt={3}>
            Our Property Clients, as &quot;data controllers&quot;, are responsible for complying
            with any regulations or laws that require providing notice, disclosure, and/or obtaining
            consent prior to collecting your personal information. If you are a resident or a
            prospective resident and have any questions or concerns about how your Property Client
            collects, stores, or uses your personal information, please contact your Property Client
            directly.
          </Text>
        </Box>

        {/* Section 11 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            11. Indemnity
          </Text>
          <Text fontSize="sm" mt={3}>
            You agree to defend, indemnify, and hold harmless Entrata, its officers, directors,
            employees, affiliates, subsidiaries, agents, licensors, and suppliers, from and against
            all third party claims, actions or demands, costs, damages, liabilities, settlements,
            and expenses, including, without limitation, reasonable legal and accounting fees,
            arising in connection with your use of the Services or resulting from, or alleged to
            result from, your use of the Services or your violation of these Terms or any law. We
            shall have the right, in our sole discretion, to select our own legal counsel to defend
            Entrata from any claims (but by doing so shall not excuse your indemnity obligations).
            You shall notify Entrata immediately if you become aware of any actual or potential
            claims, suits, actions, allegations or charges that could affect your or Entrata&apos;s
            ability to fully perform their respective duties or to exercise its rights under these
            Terms. You shall not, without the prior written approval of Entrata, settle, dispose or
            enter into any proposed settlement or resolution of any claim (whether having been
            finally adjudicated or otherwise) brought against you, if such settlement or resolution
            results in any obligation or liability for Entrata.
          </Text>
        </Box>

        {/* Section 12 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            12. DISCLAIMERS OF WARRANTIES.
          </Text>
          <Text fontSize="sm" mt={3} fontWeight="bold">
            ENTRATA PROVIDES THE SERVICES &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;. TO THE
            MAXIMUM EXTENT PERMITTED BY LAW, WE MAKE NO REPRESENTATIONS, WARRANTIES OR CONDITIONS OF
            ANY KIND, EXPRESS OR IMPLIED, AS TO THE OPERATION OF THE SITE OR THE INFORMATION,
            SOFTWARE, OR CONTENT INCLUDED IN THE SITE. WE MAKE NO REPRESENTATIONS OR WARRANTIES THAT
            THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, VIRUS OR CONTAMINATION-FREE, SECURE, OR
            TIMELY. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE EXPRESSLY DISCLAIM ALL
            REPRESENTATIONS, WARRANTIES OR CONDITIONS OF ANY KIND (INCLUDING WARRANTIES MADE
            ORALLY), WHETHER EXPRESS OR IMPLIED, INCLUDING THE IMPLIED REPRESENTATIONS, WARRANTIES
            OR CONDITIONS OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
            NON-INFRINGEMENT AND THOSE ARISING FROM A COURSE OF DEALING, TRADE, USAGE OR
            PERFORMANCE. SOME JURISDICTIONS DO NOT ALLOW LIMITATIONS ON IMPLIED WARRANTIES, AND
            THEREFORE SOME OF THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU. WE URGE YOU TO KEEP BACKUP
            COPIES OF YOUR PERSONAL CONTENT, IF ANY, THAT YOU MAINTAIN ON OR USE WITH THE SITE. IF
            YOUR USE OF THE SITE RESULTS IN THE NEED FOR SERVICING OR REPLACING PROPERTY, MATERIAL,
            EQUIPMENT OR DATA, WE ARE NOT RESPONSIBLE FOR ANY RESULTING COSTS OR DAMAGES. LATITUDE
            AND LONGITUDE CONTENT IS PROVIDED &quot;AS IS,&quot; &quot;AS AVAILABLE,&quot;
            &quot;WITH ALL FAULTS,&quot; AND WITHOUT WARRANTY OF ANY KIND.
          </Text>
        </Box>

        {/* Section 13 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            13. Waiver and Limitation of Liability
          </Text>
          <Text fontSize="sm" mt={3}>
            YOUR ACCESS AND USE OF THE SERVICES IS AT YOUR OWN RISK. To the fullest extent permitted
            by law, you hereby waive and release Entrata, its employees, agents, officers, or
            representatives from any and all claims, losses, costs, or damages of any nature
            whatsoever resulting from or in any way related to your use of the Services or these
            Terms. It is intended that this limitation apply to any and all liability or cause of
            action however alleged or arising. We will not be liable to you for loss of profit or
            revenue, loss of business opportunity, loss of use, product liability, or for any
            incidental, special, indirect, punitive or consequential damages arising out of or in
            connection with your use of the Services or these Terms, even if advised of the
            possibility of such damages and even in the event of fault, tort (including negligence)
            or strict or product liability.
          </Text>
          <Text fontSize="sm" mt={3}>
            We will not be liable for performance of services where delayed by war, riot, epidemic,
            pandemic, network failures, malicious cyberattacks, interruptions in third party
            telecommunications, energy, acts of God or governmental action, embargoes, strikes, or
            acts of our vendors and suppliers, concealed acts of workmen (whether ours or others),
            or accidents. The indemnification provided for in this section shall survive any
            termination of these Terms.
          </Text>
          <Text fontSize="sm" mt={3} fontWeight="bold">
            WITHOUT LIMITING THE FOREGOING, TO THE EXTENT PERMITTED BY LAW, IN NO EVENT WILL THE
            AGGREGATE LIABILITY TO YOU ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR THE
            SERVICES EXCEED THE AMOUNT OF FEES PAID BY YOU TO US IN CONNECTION WITH PAYMENTS MADE
            VIA THE PAYMENT GATEWAY.
          </Text>
        </Box>

        {/* Section 14 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            14. Dispute Resolution &amp; Venue Selection.
          </Text>
          <Text fontSize="sm" mt={3}>
            Any claim, dispute, or controversy between you and us arising out of, in relation to, or
            in connection with these Terms or the Services, including claims regarding applicability
            or validity of this arbitration provision (each, a &quot;Claim&quot;), shall be resolved
            by binding arbitration administered by the American Arbitration Association
            (&quot;AAA&quot;) in accordance with its Consumer Arbitration Rules. This agreement to
            arbitrate (this &quot;Arbitration Agreement&quot;) applies to all Claims between you and
            Entrata, including our affiliates, subsidiaries, parents, successors and assigns, and
            each of our and their respective officers, directors, employees, agents, and
            shareholders. This Arbitration Agreement also applies to Claims between you and your
            property manager or property owner, who shall be considered an intended third-party
            beneficiary of this Arbitration Agreement. This Arbitration Agreement is made pursuant
            to a transaction involving interstate commerce. In all other respects, including without
            limitation the determination of any questions about whether Claims are within the scope
            of this Arbitration Agreement and therefore subject to arbitration, shall be governed by
            the Federal Arbitration Act, 9 U.S.C. §1-16 (&quot;FAA&quot;) and resolved by
            interpreting the Arbitration Agreement in the broadest way that applicable law will
            allow it to be construed, to the exclusion of any state or municipal law of arbitration.
            If the FAA is inapplicable for any reason, then this Arbitration Agreement is governed
            by the laws of the State of Utah, other than such laws, rules, regulations, and case law
            that would result in the application of the laws of a jurisdiction other than the State
            of Utah.
          </Text>
          <Text fontSize="sm" mt={3}>
            All Claims are subject to arbitration, no matter what theory they are based on or what
            remedy they seek, except as expressly set forth in this Arbitration Agreement (including
            Claims based on contract, tort, fraud, agency, negligence, statutory or regulatory
            provisions, or any other source of law). As an exception to arbitration, you and Entrata
            retain the right to pursue in a small claims court located in the federal judicial
            district that includes your billing address at the time of the Claim for any Claim that
            is within such court&apos;s jurisdiction and proceeds on an individual basis. Nothing in
            this Arbitration Agreement bars either you or Entrata from bringing issues to the
            attention of federal, state, or local agencies.
          </Text>
          <Text fontSize="sm" mt={3}>
            This Arbitration Agreement applies to all Claims now in existence or that may arise in
            the future. This Arbitration Agreement shall survive any termination of these Terms or
            conclusion of your relationship with Entrata.
          </Text>
          <Text fontSize="sm" mt={3}>
            You and Entrata agree that any claim or dispute within the scope of this Arbitration
            Agreement shall be arbitrated on an individual basis, shall not be combined or
            consolidated with a dispute involving service provided to any other person or entity,
            and shall not under any circumstances proceed as part of a class action. You and Entrata
            agree that the AAA Supplementary Rules for Multiple Case Filings shall not apply to any
            claim or dispute within the scope of this Arbitration Agreement.
          </Text>
          <Text fontSize="sm" mt={3} fontWeight="bold">
            IF FOR ANY REASON A CLAIM PROCEEDS IN COURT RATHER THAN IN ARBITRATION, YOU AND ENTRATA
            EACH UNCONDITIONALLY WAIVE ANY RIGHT TO A JURY TRIAL. THIS MEANS THAT ANY CLAIM WOULD BE
            DECIDED BY A JUDGE, NOT A JURY. FURTHER, WITH RESPECT TO ANY CLAIM THAT PROCEEDS IN A
            COURT, YOU AND WE AGREE THAT EACH OF US MAY BRING CLAIMS AGAINST THE OTHER ONLY ON AN
            INDIVIDUAL BASIS AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, OR
            REPRESENTATIVE OR PRIVATE ATTORNEY GENERAL ACTION OR PROCEEDING.
          </Text>
          <Text fontSize="sm" mt={3}>
            The arbitration shall be conducted before a single arbitrator, applying the substantive
            laws of the State of Utah, other than such laws, rules, regulations and case law that
            would result in the application of the laws of a jurisdiction other than the State of
            Utah. The arbitrator&apos;s authority is limited solely to the Claims between you and us
            alone. The arbitrator will have no authority to award punitive, consequential, or other
            damages not measured by the prevailing party&apos;s actual damages. If you prevail in
            the arbitration of any Claim against us, we will reimburse you for any fees that you
            paid to the AAA in connection with the arbitration, but not your attorney or other legal
            fees. Any decision rendered will be final and binding on the parties, and judgment may
            be entered in a court of competent jurisdiction.
          </Text>
          <Text fontSize="sm" mt={3}>
            In order to initiate arbitration, you must follow the applicable provisions of the AAA
            Consumer Arbitration Rules. Arbitration rules and forms may be obtained from the AAA at
            http://www.adr.org/. Claims shall be filed in any AAA office. The place and legal seat
            of the arbitration shall be in Salt Lake City, Utah, unless the arbitrator determines
            that another location would be more appropriate in accordance with the applicable AAA
            rules. Notice of such arbitration may be provided to us at the following address: Attn:
            Legal Department, Entrata, Inc., 4205 Chapel Ridge Road, Lehi, UT 84048.
          </Text>
          <Text fontSize="sm" mt={3}>
            With the exception of any of the prohibition of class and representative actions and
            non-individualized relief provisions above, if a court of competent jurisdiction decides
            that any part of this Arbitration Agreement is invalid or unenforceable, the other parts
            of this Arbitration Agreement shall still apply. If a court of competent jurisdiction
            decides that any of the prohibition of class and representative actions and
            non-individualized relief provisions above is invalid or unenforceable because it would
            prevent the exercise of a non-waivable right to pursue public injunctive relief, then
            any dispute regarding the entitlement to such relief (and only that relief) must be
            severed from this Arbitration Agreement and may be litigated in court. All other
            disputes subject to arbitration under the terms of this Arbitration Agreement shall be
            arbitrated under its terms.
          </Text>
          <Text fontSize="sm" mt={3}>
            If you are a new user, you can choose to reject this Arbitration Agreement by notifying
            us in writing within 30 days after you accept these Terms for the first time. You must
            send your opt-out notice by certified mail to: Arbitration Opt-Out, Attn: Legal
            Department, Entrata, Inc., 4205 Chapel Ridge Road, Lehi, UT 84048.
          </Text>
          <Text fontSize="sm" mt={3}>
            The opt-out notice must include your first name, last name, mailing address, phone
            number, the email address used to log in to your account (if applicable), along with a
            statement that you decline this Arbitration Agreement. You must sign the opt-out notice
            for it to be effective. This procedure is the only way you can opt out of this
            Arbitration Agreement. If you opt out of this Arbitration Agreement, all other terms of
            these Terms will continue to apply. Opting out of this Arbitration Agreement has no
            effect on any previous, other, or future arbitration agreements that you may have with
            us.
          </Text>
        </Box>

        {/* Section 15 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            15. Miscellaneous
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            15.1 Assignment
          </Text>
          <Text fontSize="sm" mt={2}>
            These Terms are personal to you and you may not transfer, assign, or delegate them to
            anyone without the express written permission of Entrata. Any attempt by you to assign,
            transfer or delegate these Terms without the express written permission of Entrata will
            be null and void. We have the right to transfer, assign and delegate these Terms to one
            or more third parties without your permission.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            15.2 Entire Agreement
          </Text>
          <Text fontSize="sm" mt={2}>
            These Terms constitute the sole and entire agreement between you and Entrata with
            respect to the Services, and supersede all prior and contemporaneous understandings,
            agreements, representations and warranties, both written and oral, with respect to the
            Services. If any part of these Terms is determined to be invalid or unenforceable, then
            the invalid or unenforceable provision will be deemed superseded by a valid, enforceable
            provision that most closely matches the intent of the original provision, and the
            remainder of the Terms will continue in effect. The Terms (including any related
            consents or agreements that you provide during your visit to the Services) constitutes
            the entire agreement between you and us with respect to the Services and supersedes all
            other prior or contemporaneous communications and proposals, whether electronic, oral or
            written, between you and us regarding the Services and/or any order you place through
            it.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            15.3 No Waiver
          </Text>
          <Text fontSize="sm" mt={2}>
            The failure of any party to exercise or enforce any right or provision of these Terms,
            including any failure to act with respect to a breach, will not constitute a waiver of
            such right or provision or that party&apos;s right to act with respect to subsequent or
            similar breaches. We suggest that you print out a copy of these Terms for your records.
            Entrata and the Property Client make every effort to comply with all payment processing
            rules and regulations.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            15.4 Translation
          </Text>
          <Text fontSize="sm" mt={2}>
            Whereas we have provided the ability to translate these Terms into languages other than
            English, this translation is provided for convenience only. Your relationship with us
            and the Property Client will be governed by the English language version of the Terms,
            and if there is any contradiction between the English language version and a translated
            version, the English language version shall take precedence.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            15.5 Opt-Out or Change Information
          </Text>
          <Text fontSize="sm" mt={2}>
            Requests to change your personally identifiable information may be submitted using the
            contact information below. We may send email regarding our services. If you wish to
            discontinue such communications, follow the directions contained in the email to remove
            a name from our mailing list.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            15.6 Governing Law
          </Text>
          <Text fontSize="sm" mt={2}>
            These Terms shall be governed by the laws of the State of Utah, without giving effect to
            the choice of law provisions thereof.
          </Text>
          <Text fontSize="sm" mt={2}>
            You agree that any cause of action arising out of or relating to these Terms must
            commence within one year after the cause of action arose; otherwise, such cause of
            action is permanently barred.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            15.7 Contact
          </Text>
          <Text fontSize="sm" mt={2}>
            For questions or concerns about the use of personal information, please contact your
            Property Client. For questions about these Terms, please contact support@entrata.com.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            15.8 iOS Terms
          </Text>
          <Text fontSize="sm" mt={2}>
            This Section 15.8 only applies to the extent you are using our mobile application on an
            iOS device. You acknowledge that these Terms are between you and Entrata only, not with
            Apple Inc. (&quot;Apple&quot;), and Apple is not responsible for the Services or the
            content thereof. Apple has no obligation to furnish any maintenance and support services
            with respect to the Services. If the Services fail to conform to any applicable
            warranty, you may notify Apple and Apple will refund any applicable purchase price for
            the mobile application to you; and, to the maximum extent permitted by applicable law,
            Apple has no other warranty obligation with respect to the Services. Apple is not
            responsible for addressing any claims by you or any third party relating to the Services
            or your possession and/or use of the Services, including: (a) product liability claims;
            (b) any claim that the Services fails to conform to any applicable legal or regulatory
            requirement; or (c) claims arising under consumer protection or similar legislation.
            Apple is not responsible for the investigation, defense, settlement, and discharge of
            any third-party claim that the Services and/or your possession and use of the Services
            infringe a third party&apos;s intellectual property rights. You agree to comply with any
            applicable third-party terms when using the Services. Apple and Apple&apos;s
            subsidiaries are third-party beneficiaries of these Terms, and upon your acceptance of
            these Terms, Apple will have the right (and will be deemed to have accepted the right)
            to enforce these Terms against you as a third-party beneficiary of these Terms. You
            hereby represent and warrant that: (i) you are not located in a country that is subject
            to a U.S. Government embargo, or that has been designated by the U.S. Government as a
            &quot;terrorist supporting&quot; country; and (ii) you are not listed on any U.S.
            Government list of prohibited or restricted parties.
          </Text>
        </Box>

        {/* Section 16 */}
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold">
            16. Additional Terms
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            Entrata Text Message System
          </Text>
          <Text fontSize="sm" mt={2}>
            These Entrata Text Message System terms (&quot;Text Terms&quot;) are expressly
            incorporated into and made a part of the Terms. Capitalized terms used but not defined
            in these Text Terms will have the same meaning given in the Terms, as applicable.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            Quick Help
          </Text>
          <Text fontSize="sm" mt={2}>
            The message you received on your phone was sent to you by a Property Client because you
            opted in to receive messages either on a property&apos;s website, by sending a text with
            a keyword, by providing your phone number, or otherwise consented. Entrata Text Message
            System facilitates communication between property management companies and their
            customers. Message and data rates may apply. Federal law provides an express exception
            from the consent requirement for communications that are made for &quot;emergency
            purposes.&quot; Federal regulations define &quot;emergency purposes&quot; as
            &quot;situation[s] affecting the health and safety of consumers.&quot;
          </Text>
          <Text fontSize="sm" mt={2}>
            If you believe the message reached you in error, please do one of the following:
          </Text>
          <UnorderedList mt={2} spacing={2}>
            <ListItem fontSize="sm">
              Text HELP to 51378 for help via text message. We will respond with instructions on how
              to use our service as well as how to unsubscribe.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOP to 51378 to end all communication via text message. We will send you a text
              message to confirm that you have been unsubscribed. After this, you will no longer
              receive text messages from us. If you want to join again, just sign up as you did the
              first time and we will start sending text messages to you again.
            </ListItem>
          </UnorderedList>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            Alert Management
          </Text>
          <Text fontSize="sm" fontWeight="bold" mt={2}>
            Residents
          </Text>
          <Text fontSize="sm" mt={2}>
            If you are a resident of the Property Client sending you text messages, you can manage
            all notifications from within ResidentPortal™. Log in to your property and go to your
            My Profile tab. Under the text messaging settings block, click &quot;Edit&quot; and
            un-check the services you would like to opt-out of. You can also manage which messages
            you would like to be subscribed to in the same place.
          </Text>
          <Text fontSize="sm" mt={2}>
            As a resident you have the following commands:
          </Text>
          <UnorderedList mt={2} spacing={1}>
            <ListItem fontSize="sm">
              Text HELP to 51378 for instructions on how to use our service as well as how to
              unsubscribe.
            </ListItem>
            <ListItem fontSize="sm">Text MENU to 51378 for a list of STOP keywords.</ListItem>
            <ListItem fontSize="sm">
              Text PROPERTY to 51378 for a list of supported keywords.
            </ListItem>
            <ListItem fontSize="sm">Text RENT to 51378 to opt in to rent reminders.</ListItem>
            <ListItem fontSize="sm">
              Text STOP RENT to 51378 to stop receiving rent reminders.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOP FIX to 51378 to stop receiving maintenance updates.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOP NOTE to 51378 to stop receiving property updates.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOP PARCEL to 51378 to stop receiving parcel alerts.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOP, STOP ALL or STOPALL to 51378 to stop all communication with Entrata Text
              Message System.
            </ListItem>
          </UnorderedList>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            Non-Residents
          </Text>
          <Text fontSize="sm" mt={2}>
            If you are a non-resident or resident, you have the following commands:
          </Text>
          <UnorderedList mt={2} spacing={1}>
            <ListItem fontSize="sm">
              Text HELP to 51378 for instructions on how to use our service as well as how to
              unsubscribe.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOP INFO to 51378 to stop receiving property information messages.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOP APPUPDATES to 51378 to stop receiving application updates.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOP, STOP ALL or STOPALL to 51378 to stop all communication with Entrata Text
              Message System.
            </ListItem>
          </UnorderedList>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            Support Ticket Updates
          </Text>
          <Text fontSize="sm" mt={2}>
            If you have opted for text messaging updates while creating a support ticket, you have
            the following commands:
          </Text>
          <UnorderedList mt={2} spacing={1}>
            <ListItem fontSize="sm">
              Text REOPENTICKET ###### to 51378 to reopen the ticket. eg REOPENTICKET 123456.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOPTICKET ###### to 51378 to stop receiving ticket updates. eg STOPTICKET
              123456.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOPALLTICKET to 51378 to stop receiving all ticket updates.
            </ListItem>
            <ListItem fontSize="sm">
              Text STOP, STOP ALL or STOPALL to 51378 to stop all communication with Entrata Text
              Message System.
            </ListItem>
          </UnorderedList>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            FAQs
          </Text>
          <Text fontSize="sm" fontWeight="bold" mt={2}>
            How did I get subscribed to your service?
          </Text>
          <Text fontSize="sm" mt={1}>
            There are a few ways you can opt-in to our services. The first is via the web (on a
            form, for example, indicating interest in leasing a residential property). The second is
            by texting a keyword to the shortcode, 51378. Lastly, you may have provided your phone
            number to a Property Client or otherwise provided consent to opt-in.
          </Text>
          <Text fontSize="sm" fontWeight="bold" mt={2}>
            Am I being charged for these messages?
          </Text>
          <Text fontSize="sm" mt={1}>
            That depends on the standard text messaging rate plan you have set up with your
            carrier/provider. Neither Entrata nor a Property Client are directly charging you to
            receive these messages. However, standard message and data rates may apply.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            Terms and Conditions
          </Text>
          <Text fontSize="sm" mt={2}>
            You must be 18 years or older to receive/send text messages in affiliation with any of
            these subscriptions. By your participation in these property subscriptions, you certify
            that you are at least 18 years of age and meet any other eligibility and residency
            requirements of the specific property.
          </Text>
          <Text fontSize="sm" mt={2}>
            You acknowledge that text messages are distributed via third party mobile network
            providers and, therefore, the Property Client cannot control certain factors relating to
            message delivery. You acknowledge that, depending on the recipient&apos;s mobile
            provider service, it may not be possible to transmit the text message to the recipient
            successfully. Carriers are not liable for delayed or undelivered messages.
          </Text>
          <Text fontSize="sm" mt={2}>
            Property Client does not claim or guarantee availability or performance of this service,
            including liability for transmission delays or message failures.
          </Text>
          <Text fontSize="sm" mt={2}>
            If you opt-in or sign up to receive text messages, you thereby agree to receive text
            messaging service messages at the phone number you provide for such purposes. Such
            messages will come from Entrata.
          </Text>
          <Text fontSize="sm" mt={2}>
            Participation in our service is free of charge but standard carrier text messaging
            charges will apply. You may opt out at any time by following the opt-out instructions
            above. You represent that you are the owner or authorized user of the wireless device
            you use to sign up for the text messaging service, and that you are authorized to
            approve the applicable charges. Entrata will never rent or sell your phone number to any
            third party, nor will we use your phone number to initiate a call or text message to you
            without your express or implied prior consent. Your wireless carrier and other service
            providers also collect data about your text message usage, and their practices are
            governed by their own privacy policies.
          </Text>
          <Text fontSize="sm" mt={2}>
            You acknowledge and agree that the text messaging service is provided via wireless
            systems which use radios (and other means) to transmit communications over complex
            networks. We do not guarantee that your use of the text messaging service will be
            private or secure, and we are not liable to you for any lack of privacy or security you
            may experience. You are fully responsible for taking precautions and providing security
            measures best suited for your situation and intended use of the text messaging service.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            FEES
          </Text>
          <Text fontSize="sm" mt={2}>
            Neither Entrata nor the Property Client charges a fee for any of the subscriptions
            listed above.
          </Text>
          <Text fontSize="sm" mt={2}>
            Standard message and data rates may apply for any messages sent to you from the Property
            Client and to the Property Client from you. If you have any questions about your text
            plan or data plan, it is best to contact your wireless provider.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            FREQUENCY
          </Text>
          <Text fontSize="sm" mt={2}>
            Message frequency may vary.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={3}>
            Additional Help
          </Text>
          <Text fontSize="sm" mt={2}>
            For all questions, please contact the Property Client you receive these messages from
            directly. Alternatively, you may email support@entrata.com.
          </Text>
        </Box>
      </Box>
    </CommonModal>
  );
};

export default TermsOfUse;
